import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { employeeService } from "../../services/EmployeeService";
import FinancialAccountService from "../../services/FinancialAccountService";

export default function EmployeeDetailPage() {
    const [mounted, setMounted] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);

    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    // Payment Form States
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [paymentType, setPaymentType] = useState("Nakit");
    const [paymentDate, setPaymentDate] = useState(new Date());
    const [paymentDescription, setPaymentDescription] = useState("");
    const [financialAccounts, setFinancialAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [transactionType, setTransactionType] = useState(2); // Maaş Ödemesi

    useEffect(() => {
        setMounted(true);
    }, []);

    const fetchDetail = useCallback(async () => {
        try {
            setLoading(true);

            const res = await employeeService.getDetail(id);

            if (res.data.success) {
                setDetail(res.data.data);
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Hata",
                    detail: res.data.message
                });
            }
        } catch (err) {
            console.error("Detay getirilirken hata:", err);

            toast.current?.show({
                severity: "error",
                summary: "Hata",
                detail: "Çalışan detayları yüklenemedi"
            });
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDetail();

        FinancialAccountService.getAll()
            .then((res) => {
                const response = res?.data ?? res;

                if (response?.success) {
                    setFinancialAccounts(response.data || []);
                }
            })
            .catch((err) => {
                console.error("Finansal hesaplar yüklenirken hata:", err);
            });
    }, [fetchDetail]);

    const paymentTypes = [
        { label: "Nakit", value: "Nakit" },
        { label: "Havale/EFT", value: "Havale/EFT" },
        { label: "Kredi Kartı", value: "Kredi Kartı" }
    ];

    const employeeTransactionTypes = [
        { label: "Maaş Ödemesi", value: 2 },
        { label: "Avans Ödemesi", value: 3 },
        { label: "Prim", value: 4 },
        { label: "Gider Geri Ödemesi", value: 7 },
        { label: "Düzeltme", value: 8 }
    ];

    const filteredAccounts = useMemo(() => {
        if (!financialAccounts || financialAccounts.length === 0) return [];

        if (paymentType === "Nakit") {
            return financialAccounts.filter((a) => Number(a.accountType) === 1);
        }

        if (paymentType === "Havale/EFT") {
            return financialAccounts.filter((a) => Number(a.accountType) === 2);
        }

        if (paymentType === "Kredi Kartı") {
            return financialAccounts.filter((a) => Number(a.accountType) === 3);
        }

        return [];
    }, [financialAccounts, paymentType]);

    useEffect(() => {
        if (filteredAccounts.length === 1) {
            setSelectedAccountId(filteredAccounts[0].id);
        } else {
            setSelectedAccountId(null);
        }
    }, [filteredAccounts]);

    const handleReceivePayment = async () => {
        if (!paymentAmount || paymentAmount <= 0) {
            toast.current?.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "Lütfen ödeme tutarı girin",
                life: 3000
            });
            return;
        }

        try {
            setSubmitting(true);

            const payload = {
                employeeId: parseInt(id),
                amount: paymentAmount,
                paymentType: paymentType,
                paymentDate: paymentDate,
                description: paymentDescription,
                financialAccountId: selectedAccountId,
                transactionType: transactionType
            };

            const res = await employeeService.receivePayment(payload);

            if (res.data.success) {
                setPaymentAmount(0);
                setPaymentType("Nakit");
                setPaymentDate(new Date());
                setPaymentDescription("");
                setSelectedAccountId(null);
                setTransactionType(2);
                setShowPaymentDialog(false);

                toast.current?.show({
                    severity: "success",
                    summary: "Başarılı",
                    detail: "Ödeme kaydedildi",
                    life: 3000
                });

                await fetchDetail();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Hata",
                    detail: res.data.message
                });
            }
        } catch (err) {
            console.error("Ödeme alınırken hata:", err);

            toast.current?.show({
                severity: "error",
                summary: "Hata",
                detail: "Ödeme alınırken hata oluştu",
                life: 3000
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-[80vh] animate-fade-in">
                <i className="pi pi-spin pi-spinner text-4xl text-indigo-600"></i>
                <p className="mt-4 text-slate-600 font-medium">Çalışan bilgileri yükleniyor...</p>
            </div>
        );
    }

    if (!detail || !detail.employee) {
        return (
            <div className="p-6 animate-fade-in">
                <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-8 text-center max-w-lg mx-auto">
                    <i className="pi pi-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Çalışan bulunamadı</h3>
                    <Button
                        label="Listeye Dön"
                        icon="pi pi-arrow-left"
                        className="p-button-text"
                        onClick={() => navigate("/employees")}
                    />
                </div>
            </div>
        );
    }

    const { employee, transactions = [] } = detail;

    const overlayAppendTo = mounted ? document.body : "self";

    const dropdownOverlayProps = {
        appendTo: overlayAppendTo,
        autoZIndex: false,
        baseZIndex: 12000,
        panelClassName: "employee-payment-overlay"
    };

    const getTransactionTypeLabel = (type) => {
        const types = {
            1: "Maaş Tahakkuku",
            2: "Maaş Ödemesi",
            3: "Avans Ödemesi",
            4: "Prim",
            5: "Kesinti",
            6: "Çalışan Cebinden Gider",
            7: "Gider Geri Ödemesi",
            8: "Düzeltme"
        };

        return types[type] || "Bilinmiyor";
    };

    const getTransactionSeverity = (type) => {
        const severities = {
            1: "info",
            2: "success",
            3: "warning",
            4: "success",
            5: "danger",
            6: "help",
            7: "info",
            8: "secondary"
        };

        return severities[type] || "secondary";
    };

    const amountTemplate = (rowData) => {
        const isOut = rowData.direction === 2 || rowData.direction === 3;

        return (
            <span className={`font-bold ${isOut ? "text-red-500" : "text-green-600"}`}>
                {isOut ? "-" : "+"}{" "}
                {Number(rowData.amount || 0).toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}{" "}
                ₺
            </span>
        );
    };

    return (
        <div className="animate-fade-in min-h-screen">
            <style>
                {`
                    .employee-payment-overlay {
                        z-index: 12000 !important;
                    }

                    .p-dropdown-panel.employee-payment-overlay,
                    .p-datepicker.employee-payment-overlay {
                        z-index: 12000 !important;
                    }

                    .p-dialog-mask {
                        z-index: 9999 !important;
                    }

                    .p-dialog {
                        z-index: 10000 !important;
                    }
                `}
            </style>

            <Toast ref={toast} appendTo={overlayAppendTo} />

            {/* HEADER SECTION */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 rounded-3xl p-6 mb-6 text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>

                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <Button
                                icon="pi pi-arrow-left"
                                className="p-button-rounded p-button-text text-white hover:bg-white/10"
                                onClick={() => navigate("/employees")}
                            />

                            <div className="w-16 h-16 rounded-2xl bg-indigo-500 flex items-center justify-center shadow-lg">
                                <i className="pi pi-user text-3xl"></i>
                            </div>

                            <div>
                                <h1 className="text-2xl md:text-3xl font-black m-0 tracking-tight">
                                    {employee.fullName}
                                </h1>

                                <div className="flex items-center gap-2 mt-1">
                                    <Tag
                                        value={employee.position || "Pozisyon Belirtilmemiş"}
                                        className="bg-white/20 text-white text-[10px] font-bold"
                                    />
                                    <span className="text-slate-400 text-xs font-medium">#{employee.id}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                label="Ödeme Yap"
                                icon="pi pi-plus-circle"
                                className="p-button-success !rounded-xl font-bold"
                                onClick={() => setShowPaymentDialog(true)}
                            />

                            <Button
                                label="Düzenle"
                                icon="pi pi-user-edit"
                                className="p-button-warning !rounded-xl font-bold"
                                onClick={() => navigate(`/employees/edit/${id}`)}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-sm">
                            <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                                Güncel Bakiye
                            </span>

                            <div className={`text-2xl font-black mt-1 ${employee.balance < 0 ? "text-rose-400" : "text-emerald-400"}`}>
                                ₺
                                {Math.abs(Number(employee.balance || 0)).toLocaleString("tr-TR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}

                                <span className="text-xs ml-2 font-bold opacity-60">
                                    {employee.balance < 0
                                        ? "(Çalışan Alacaklı)"
                                        : employee.balance > 0
                                            ? "(Kurum Alacaklı)"
                                            : "(Dengede)"}
                                </span>
                            </div>
                        </div>

                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-sm">
                            <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                                Maaş Bilgisi
                            </span>

                            <div className="text-2xl font-black mt-1 text-white">
                                ₺
                                {Number(employee.salaryAmount || 0).toLocaleString("tr-TR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}

                                <span className="text-xs ml-2 font-bold text-slate-400">/ Ay</span>
                            </div>
                        </div>

                        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-sm">
                            <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                                İletişim
                            </span>

                            <div className="text-lg font-bold mt-1 text-white flex items-center gap-2">
                                <i className="pi pi-phone text-indigo-400"></i>
                                {employee.phoneNumber || "—"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* INFO CARDS */}
                <div className="lg:col-span-4 space-y-6">
                    <Card title="Personel Bilgileri" className="shadow-lg rounded-2xl border border-slate-200">
                        <div className="space-y-4">
                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">E-Posta</span>
                                <span className="text-slate-900 font-bold text-sm">{employee.email || "—"}</span>
                            </div>

                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">T.C. No</span>
                                <span className="text-slate-900 font-bold text-sm">{employee.identityNumber || "—"}</span>
                            </div>

                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">SGK No</span>
                                <span className="text-slate-900 font-bold text-sm">{employee.socialSecurityNumber || "—"}</span>
                            </div>

                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">İşe Başlama</span>
                                <span className="text-slate-900 font-bold text-sm">
                                    {employee.startDate
                                        ? new Date(employee.startDate).toLocaleDateString("tr-TR")
                                        : "—"}
                                </span>
                            </div>

                            <div className="flex flex-col gap-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">Adres</span>
                                <span className="text-slate-900 font-medium text-sm bg-slate-50 p-2 rounded-lg border border-slate-100">
                                    {employee.address || "Adres bilgisi yok"}
                                </span>
                            </div>
                        </div>
                    </Card>

                    <Card title="Finansal Bilgiler" className="shadow-lg rounded-2xl border border-slate-200">
                        <div className="space-y-4">
                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">IBAN</span>
                                <span className="text-slate-900 font-bold text-sm">{employee.iban || "—"}</span>
                            </div>

                            <div className="flex justify-between border-b border-slate-50 pb-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">Ödeme Günü</span>
                                <span className="text-slate-900 font-bold text-sm">
                                    Her Ayın {employee.salaryPaymentDay || "—"}. Günü
                                </span>
                            </div>

                            <div className="flex flex-col gap-2">
                                <span className="text-slate-500 font-bold text-xs uppercase">Not</span>
                                <span className="text-slate-600 italic text-xs bg-amber-50 p-3 rounded-lg border border-amber-100">
                                    {employee.note || "Eklenmiş bir not bulunmuyor."}
                                </span>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* TRANSACTION HISTORY */}
                <div className="lg:col-span-8">
                    <Card
                        title={
                            <div className="flex items-center justify-between">
                                <span>İşlem Geçmişi</span>
                                <Tag value={`${transactions.length} İŞLEM`} severity="info" className="font-black" />
                            </div>
                        }
                        className="shadow-lg rounded-2xl border border-slate-200 h-full"
                    >
                        <DataTable
                            value={transactions}
                            paginator
                            rows={10}
                            emptyMessage="Henüz bir işlem kaydı bulunmuyor."
                            responsiveLayout="scroll"
                            className="p-datatable-sm"
                            stripedRows
                        >
                            <Column
                                field="transactionDate"
                                header="TARİH"
                                body={(r) => new Date(r.transactionDate).toLocaleDateString("tr-TR")}
                                sortable
                                className="text-xs font-bold text-slate-500"
                            />

                            <Column
                                field="transactionType"
                                header="İŞLEM TİPİ"
                                body={(r) => (
                                    <Tag
                                        value={getTransactionTypeLabel(r.transactionType)}
                                        severity={getTransactionSeverity(r.transactionType)}
                                        className="text-[10px] font-black uppercase px-2 py-0.5"
                                    />
                                )}
                            />

                            <Column
                                field="description"
                                header="AÇIKLAMA"
                                className="text-xs text-slate-600"
                            />

                            <Column
                                field="amount"
                                header="TUTAR"
                                body={amountTemplate}
                                align="right"
                                className="font-black"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>

            {/* Ödeme Yap Dialog */}
            <Dialog
                header={
                    <div className="flex items-center gap-3">
                        <div className="!bg-emerald-100 p-2 rounded-lg">
                            <i className="pi pi-plus-circle text-emerald-600 text-xl"></i>
                        </div>

                        <div>
                            <h3 className="text-lg font-black text-slate-900 m-0">Ödeme Yap</h3>
                            <p className="text-xs text-slate-500 font-medium m-0">Çalışana ödeme girişi yapın</p>
                        </div>
                    </div>
                }
                visible={showPaymentDialog}
                style={{ width: "450px" }}
                breakpoints={{ "960px": "85vw", "641px": "95vw" }}
                onHide={() => setShowPaymentDialog(false)}
                className="p-fluid"
                appendTo={overlayAppendTo}
                autoZIndex={false}
                baseZIndex={10000}
                blockScroll={true}
                footer={
                    <div className="flex gap-2">
                        <Button
                            label="Vazgeç"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary font-bold"
                            onClick={() => setShowPaymentDialog(false)}
                        />

                        <Button
                            label="Ödemeyi Kaydet"
                            icon="pi pi-check"
                            className="!bg-emerald-600 border-none font-black"
                            onClick={handleReceivePayment}
                            loading={submitting}
                            disabled={paymentAmount <= 0}
                        />
                    </div>
                }
            >
                <div className="space-y-4 pt-6 pb-6">
                    <div className="field">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">
                            Ödeme Tutarı (₺)
                        </label>

                        <InputNumber
                            value={paymentAmount}
                            onValueChange={(e) => setPaymentAmount(e.value || 0)}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            placeholder="0,00"
                            autoFocus
                            className="w-full"
                            inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-emerald-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 pb-5">
                        <div className="field col-span-2">
                            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">
                                İşlem Tipi
                            </label>

                            <Dropdown
                                value={transactionType}
                                options={employeeTransactionTypes}
                                onChange={(e) => setTransactionType(e.value)}
                                placeholder="Seçiniz"
                                className="rounded-xl border-slate-200"
                                {...dropdownOverlayProps}
                            />
                        </div>

                        <div className="field col-span-2">
                            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">
                                Ödeme Yöntemi
                            </label>

                            <Dropdown
                                value={paymentType}
                                options={paymentTypes}
                                onChange={(e) => setPaymentType(e.value)}
                                placeholder="Seçiniz"
                                className="rounded-xl border-slate-200"
                                {...dropdownOverlayProps}
                            />
                        </div>

                        {filteredAccounts.length > 0 && (
                            <div className="field col-span-2">
                                <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">
                                    İşlem Yapılacak Hesap
                                </label>

                                <Dropdown
                                    value={selectedAccountId}
                                    options={filteredAccounts}
                                    optionLabel="name"
                                    optionValue="id"
                                    onChange={(e) => setSelectedAccountId(e.value)}
                                    placeholder="Hesap seçin..."
                                    className="rounded-xl border-slate-200"
                                    {...dropdownOverlayProps}
                                />
                            </div>
                        )}

                        <div className="field col-span-2">
                            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">
                                İşlem Tarihi
                            </label>

                            <Calendar
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.value)}
                                dateFormat="dd.mm.yy"
                                locale="tr"
                                showIcon
                                className="w-full"
                                inputClassName="rounded-xl border-slate-200 py-2 font-bold"
                                {...dropdownOverlayProps}
                            />
                        </div>
                    </div>

                    <div className="field">
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">
                            Açıklama
                        </label>

                        <InputText
                            value={paymentDescription}
                            onChange={(e) => setPaymentDescription(e.target.value)}
                            placeholder="Ek notlar..."
                            className="rounded-xl border-slate-200"
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}